import React, { FunctionComponent } from "react";
import { View, Document, Font } from "@react-pdf/renderer";

// PDF Components
import DocumentBody from "./common/DocumentBody";
import RTABAKFooter from "./common/RTABAKFooter";
import RTABAKHeader from "./common/RTABAKHeader";
import CustomerHeader from "./common/CustomerHeader";

import MaterialHeader from "./checkout/MaterialHeader";
import DetailedItem from "./checkout/DetailedItem";

// Fonts
import MontRegular from "../../../assets/fonts/Mont-Regular.ttf";
import MontBold from "../../../assets/fonts/Mont-Bold.ttf";
import PlayfairDisplay from "../../../assets/fonts/PlayfairDisplay.ttf";

// Types
import {
  Cart,
  DetailedCartItem,
} from "../../../context/CartContext/CartContext.api.types";
import ItemsHeader from "./checkout/ItemsHeader";
import SpecialManufacturingDisclaimer from "./common/SpecialManufacturingDisclaimer";
import TotalsItems from "./checkout/TotalsItems";
import DetailedItemTotal from "./checkout/DetailedItemTotal";
import { displayMoney } from "../../../utils/displayMoney";

Font.register({
  family: "Mont-Regular",
  src: MontRegular,
});
Font.register({
  family: "Mont-Bold",
  src: MontBold,
});
Font.register({
  family: "PlayfairDisplay",
  src: PlayfairDisplay,
});

type Props = {
  user: User;
  cart: Cart;
  hasGoldVariations: boolean;
  hasSilverVariations: boolean;
  getGoldVariations: () => DetailedCartItem[] | [];
  getSilverVariations: () => DetailedCartItem[] | [];
};

const CheckoutOrderFile: FunctionComponent<Props> = ({
  user,
  cart,
  hasGoldVariations,
  hasSilverVariations,
  getGoldVariations,
  getSilverVariations,
}) => {
  const getTotalQuantity = (materialVariations: () => DetailedCartItem[]) => {
    let totalQuantity = 0;
    materialVariations().forEach((x) => (totalQuantity += x?.quantity ?? 0));
    return totalQuantity;
  };

  const totalQuantity = () =>
    getTotalQuantity(getSilverVariations) + getTotalQuantity(getGoldVariations);

  return (
    <Document>
      <DocumentBody>
        <RTABAKHeader />
        <CustomerHeader user={user} />
        <View>
          {hasGoldVariations ? (
            <>
              <MaterialHeader label={"Material Oro"} />
              <ItemsHeader />
              {getGoldVariations().map(
                ({
                  variation,
                  customization,
                  quantity,
                  product,
                  weight,
                  manufacturingCost,
                }) => (
                  <DetailedItem
                    key={variation.id}
                    weight={weight}
                    customization={customization}
                    variation={variation}
                    quantity={quantity}
                    product={product}
                    manufacturingCost={manufacturingCost * quantity}
                  />
                )
              )}
              <DetailedItemTotal
                totalWeight={cart.summary.by_material.gold.weight}
                totalHechura={displayMoney(
                  cart.summary.by_material.gold.manufacturingCost
                )}
                totalQuantity={getTotalQuantity(getGoldVariations)}
              />
            </>
          ) : null}
          {hasSilverVariations ? (
            <>
              <MaterialHeader label={"Material Plata"} />
              <ItemsHeader />
              {getSilverVariations().map(
                ({
                  variation,
                  quantity,
                  product,
                  customization,
                  weight,
                  manufacturingCost,
                }) => (
                  <DetailedItem
                    key={variation.id}
                    variation={variation}
                    weight={weight}
                    quantity={quantity}
                    customization={customization}
                    product={product}
                    manufacturingCost={manufacturingCost * quantity}
                  />
                )
              )}
              <DetailedItemTotal
                totalWeight={cart.summary.by_material.silver.weight}
                totalHechura={displayMoney(
                  cart.summary.by_material.silver.manufacturingCost
                )}
                totalQuantity={getTotalQuantity(getSilverVariations)}
              />
            </>
          ) : null}
          <TotalsItems
            totalQuantity={totalQuantity()}
            totalGold={
              hasGoldVariations ? cart.summary.by_material.gold.weight : null
            }
            totalSilver={
              hasSilverVariations
                ? cart.summary.by_material.silver.weight
                : null
            }
            totalHechura={displayMoney(cart.summary.totals.manufacturingCost)}
          />
          <SpecialManufacturingDisclaimer />
        </View>

        <RTABAKFooter />
      </DocumentBody>
    </Document>
  );
};

export default CheckoutOrderFile;
